import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { DeliveryOptions } from './DeliveryOptions';
import { FreeDeliveryToggle } from './FreeDeliveryToggle';
import { useDeliveryOptionsContext } from '../../context/DeliveryOptionsContext';
import {
  getDeliveryDimension, getSingleDeliveryRefinement, toTitleCase
} from '../../utils/deliveryUtils';
import {
  getAppliedDeliveryDimension
} from '../../utils/fulfillment-helpers';
import { DELIVERY_REFINEMENTS, FULFILLMENT_DIMENSIONS_LABELS, FULFILLMENT_LABELS } from '../../utils/constants';

const {
  bool, func, string, object
} = PropTypes;

const DeliveryRefinements = ({
  className,
  customStyles,
  onDimensionChange,
  onRefinementChange,
  skipDeliveryAutoSelect,
  addMarkupForSeo,
  deselect
}) => {
  const {
    setSelectedDeliveryDimension,
    isFreeDeliveryChecked,
    setIsFreeDeliveryChecked,
    deliveryOptions,
    hideDeliveryOptions,
    freeDeliveryUnavailable,
    appliedDimensions,
    triggerDeliverySpeedChange,
    setTriggerDeliverySpeedChange,
    dimensions,
    forceInitialSync,
    setForceInitialSync
  } = useDeliveryOptionsContext();
  const freeDeliveryRefinement = getSingleDeliveryRefinement(
    dimensions, FULFILLMENT_DIMENSIONS_LABELS.freeDelivery,
    DELIVERY_REFINEMENTS.ANYTIME
  );
  const [localIsFreeDeliveryChecked, setLocalIsFreeDeliveryChecked] = useState(isFreeDeliveryChecked);
  const [uiSelectedOption, setUiSelectedOption] = useState('');

  const syncUi = useCallback((updatedDimensions) => {
    const appliedDeliveryDimension = getAppliedDeliveryDimension(updatedDimensions);

    // Reset UI selection to allow syncing with context state
    setUiSelectedOption('');
    // Sync local free delivery toggle state and trigger selected option state based on appliedDimensions
    setLocalIsFreeDeliveryChecked(appliedDeliveryDimension?.label === FULFILLMENT_LABELS.FREE_DELIVERY);
    setTriggerDeliverySpeedChange(appliedDeliveryDimension?.refinements[0]?.label || null);
  }, [setLocalIsFreeDeliveryChecked, setTriggerDeliverySpeedChange]);

  const handleFreeDeliveryToggle = useCallback((isTrue) => {
    setLocalIsFreeDeliveryChecked(isTrue);
    const newDeliveryDimensionLabel = isTrue
      ? FULFILLMENT_LABELS.FREE_DELIVERY : FULFILLMENT_LABELS.DELIVERY;

    const appliedDeliveryDimension = getAppliedDeliveryDimension(appliedDimensions);
    const appliedRefinementLabel = appliedDeliveryDimension?.refinements[0]?.label;

    const dimension = getDeliveryDimension(
      dimensions,
      newDeliveryDimensionLabel
    );

    let refinement = getSingleDeliveryRefinement(
      dimensions,
      newDeliveryDimensionLabel,
      uiSelectedOption || appliedRefinementLabel || DELIVERY_REFINEMENTS.ANYTIME
    );

    refinement = { ...refinement, isToggle: true };
    onDimensionChange({ refinement, dimension });
  }, [
    onDimensionChange,
    appliedDimensions,
    dimensions,
    uiSelectedOption
  ]);

  const handleRefinementOptionChange = useCallback((label) => {
    setUiSelectedOption(label);

    const currentDeliveryDimension = localIsFreeDeliveryChecked
      ? FULFILLMENT_LABELS.FREE_DELIVERY : FULFILLMENT_LABELS.DELIVERY;

    if (onRefinementChange) {
      const dimension = getDeliveryDimension(
        dimensions,
        currentDeliveryDimension
      );
      const selectedRefinement = getSingleDeliveryRefinement(
        dimensions,
        currentDeliveryDimension,
        label
      );

      if (!selectedRefinement) {
        return;
      }

      onRefinementChange({
        refinement: {
          label,
          url: selectedRefinement?.url,
          refinementKey: selectedRefinement?.refinementKey
        },
        dimension: {
          ...dimension,
          label: currentDeliveryDimension
        }
      });
    }
  }, [onRefinementChange, dimensions, localIsFreeDeliveryChecked]);

  useEffect(() => {
    // Reset states if deselect is true
    if (deselect) {
      setLocalIsFreeDeliveryChecked(false);
      setIsFreeDeliveryChecked(false);
      setSelectedDeliveryDimension('delivery');
    } else if (forceInitialSync) {
      // Sync local UI with context state when forceInitialSync is true
      syncUi(appliedDimensions);
      setForceInitialSync(false);
    } else {
      // Sync local UI with context state when appliedDimensions changes
      const appliedDeliveryDimension = getAppliedDeliveryDimension(appliedDimensions);
      setLocalIsFreeDeliveryChecked(appliedDeliveryDimension?.label === FULFILLMENT_LABELS.FREE_DELIVERY);
      setTriggerDeliverySpeedChange({ value: appliedDeliveryDimension?.refinements[0]?.label, localOnly: true });
    }
  }, [
    appliedDimensions,
    deselect,
    forceInitialSync,
    setForceInitialSync,
    setIsFreeDeliveryChecked,
    setSelectedDeliveryDimension,
    syncUi,
    setTriggerDeliverySpeedChange
  ]);

  return (
    <div
      className={classNames('sui-flex sui-flex-col sui-gap-x-4 sui-gap-y-3 sui-w-full [&>label]:sui-mx-0', className)}
      style={customStyles}
    >
      {!freeDeliveryUnavailable && (
        <>
          {/* SEO-friendly hidden link - Required for SEO significance. Reach out to SEO team if wanting to change */}
          {addMarkupForSeo && (
            <div data-testid="emt-link-free-delivery">
              <a
                href={freeDeliveryRefinement?.url}
                className="sui-hidden"
                aria-hidden="true"
                tabIndex="-1"
              >
                {/* H3 element required for SEO significance. Reach out to SEO team if wanting to change */}
                <h3>{FULFILLMENT_LABELS.FREE_DELIVERY}</h3>
              </a>
            </div>
          )}
          <FreeDeliveryToggle isChecked={localIsFreeDeliveryChecked} onToggle={handleFreeDeliveryToggle} />
        </>
      )}
      {!hideDeliveryOptions && deliveryOptions?.length > 0 && (
        <DeliveryOptions
          onChange={handleRefinementOptionChange}
          options={deliveryOptions}
          skipAutoSelect={skipDeliveryAutoSelect && deselect}
          triggerDeliverySpeedChange={triggerDeliverySpeedChange}
          setTriggerDeliverySpeedChange={setTriggerDeliverySpeedChange}
          deselect={deselect}
        />
      )}
    </div>
  );
};

DeliveryRefinements.propTypes = {
  className: string,
  // eslint-disable-next-line react/forbid-prop-types
  customStyles: object,
  onDimensionChange: func.isRequired,
  onRefinementChange: func,
  skipDeliveryAutoSelect: bool,
  addMarkupForSeo: bool,
  deselect: bool
};

DeliveryRefinements.defaultProps = {
  className: '',
  customStyles: {},
  onRefinementChange: null,
  skipDeliveryAutoSelect: false,
  addMarkupForSeo: false,
  deselect: false
};

DeliveryRefinements.displayName = 'DeliveryRefinements';

export { DeliveryRefinements };